/**
 * Group Naming Utility
 *
 * Matches the C# HubGroup.cs format exactly:
 * - ServerFullName: tenant@{TenantID}:server@{ServerID}
 * - GroupFullName: tenant@{TenantID}:server@{ServerID}:group@{GroupName}
 */

let globalTenantId: number | null = null;
let tenantIdWarningShown = false;

/**
 * Get the global tenant ID
 * Returns hardcoded value 1 if not set, with console warning
 * Must be replaced with actual tenant context later
 */
export function getGlobalTenantId(): number {
  if (globalTenantId !== null) {
    return globalTenantId;
  }

  // Fallback to 1 with warning
  if (!tenantIdWarningShown) {
    console.warn(
      '[EdgeStream] TenantID not available - using hardcoded fallback value 1. ' +
      'This must be replaced with actual tenant context from your auth provider or session.'
    );
    tenantIdWarningShown = true;
  }

  return 1;
}

/**
 * Set the global tenant ID (call this from your auth/session setup)
 */
export function setGlobalTenantId(tenantId: number): void {
  globalTenantId = tenantId;
}

/**
 * Build server full name matching C# HubServer.ServerFullName format
 * Format: tenant@{TenantID}:server@{ServerID}
 */
export function buildServerFullName(serverId: string, tenantId?: number): string {
  const tid = tenantId ?? getGlobalTenantId();
  return `tenant@${tid}:server@${serverId}`;
}

/**
 * Build group full name matching C# HubGroup.GroupFullName format
 * Format: tenant@{TenantID}:server@{ServerID}:group@{GroupName}
 */
export function buildGroupFullName(
  serverId: string,
  groupName: string,
  tenantId?: number
): string {
  const serverName = buildServerFullName(serverId, tenantId);
  return `${serverName}:group@${groupName}`;
}

/**
 * Extract tenant ID from server full name
 * Format: tenant@{TenantID}:server@{ServerID}
 */
export function extractTenantIdFromServerName(serverFullName: string): number | null {
  const match = serverFullName.match(/^tenant@(\d+):/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Extract server ID from server full name
 * Format: tenant@{TenantID}:server@{ServerID}
 */
export function extractServerIdFromServerName(serverFullName: string): string | null {
  const match = serverFullName.match(/:server@(.+?)(?::|$)/);
  return match ? match[1] : null;
}

/**
 * Extract group name from group full name
 * Format: tenant@{TenantID}:server@{ServerID}:group@{GroupName}
 */
export function extractGroupNameFromGroupName(groupFullName: string): string | null {
  const match = groupFullName.match(/:group@(.+)$/);
  return match ? match[1] : null;
}
